import React, { useEffect } from "react";
import { ThemeContext, LanguageContext, UserContext } from "../contexts/Context";
import styled from "@emotion/styled";
import { Link } from "@reach/router";
import { IoArrowForwardOutline, IoRestaurantOutline } from "react-icons/io5";
import { actionTextColor, lightTint, readableText } from "../utils/themeContrast";
import Header from "../newcomponents/Header";

const Category = () => {
    const languageContext = React.useContext(LanguageContext);
    const themeContext = React.useContext(ThemeContext);
    const { lanIndex, title, menus, menuList } = React.useContext(UserContext);
    const categories = (menuList || []).filter(menu =>
        !title || !menus || menus.length <= 1 || menu.title === title
    );

    useEffect(() => {
        const onBackChange = () => window.history.pushState(null, null, "/category");
        window.addEventListener("popstate", onBackChange, false);
        return () => window.removeEventListener("popstate", onBackChange);
    }, []);

    if (typeof lanIndex === "undefined") return null;

    return (
        <>
            <Header hasBack={false} />
            <MenuWrapper themeContext={themeContext}>
                <Intro themeContext={themeContext}>{languageContext[lanIndex].bgTxt}</Intro>
                <CategoryGrid>
                    {categories.map((category, index) => {
                        const label = lanIndex === 0 ? category.category : category.category2 || category.category;
                        const image = category.catImg
                            ? (/^https?:\/\//i.test(category.catImg) ? category.catImg : `https://ireachfnb.com${category.catImg}`)
                            : null;
                        return (
                            <CategoryCard
                                key={`${category.category}-${index}`}
                                to={`/menu/menu/${encodeURIComponent(category.category)}`}
                                themeContext={themeContext}
                            >
                                <ImageFrame themeContext={themeContext}>
                                    <IoRestaurantOutline size={36} aria-hidden="true" />
                                    {image && <CategoryImage src={image} alt="" loading="lazy"
                                        onError={event => { event.currentTarget.style.display = "none"; }} />}
                                </ImageFrame>
                                <CardFooter>
                                    <CategoryTitle>{label}</CategoryTitle>
                                    <CategoryArrow themeContext={themeContext} aria-hidden="true">
                                        <IoArrowForwardOutline size={18} />
                                    </CategoryArrow>
                                </CardFooter>
                            </CategoryCard>
                        );
                    })}
                </CategoryGrid>
            </MenuWrapper>
        </>
    );
};

export default Category;

const MenuWrapper = styled.main`
  width: 100vw;
  max-width: 550px;
  height: calc(100vh - ${props => props.themeContext.headerHeight}px);
  height: calc(100dvh - ${props => props.themeContext.headerHeight}px);
  margin: ${props => props.themeContext.headerHeight}px auto 0;
  padding: 20px 16px max(24px, env(safe-area-inset-bottom));
  box-sizing: border-box;
  overflow-y: auto;
  background: ${props => props.themeContext.backgroundColor};
  font-family: ${props => props.themeContext.fontFamily};
`;

const Intro = styled.p`
  margin: 0 0 20px;
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.descriptionColor, props.themeContext.fontPrimary)};
  font-size: 14px;
  line-height: 1.6;
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 12px;
  align-items: stretch;
  @media (max-width: 319px) { grid-template-columns: minmax(0, 1fr); }
`;

const CategoryCard = styled(Link)`
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
  border: 1px solid ${props => lightTint(props.themeContext.primaryColor, .18)};
  border-radius: 16px;
  background: ${props => props.themeContext.backgroundColor};
  color: ${props => readableText(props.themeContext.backgroundColor, props.themeContext.menuItemNameColor, props.themeContext.primaryColor)};
  text-decoration: none;
  box-shadow: 0 3px 12px rgba(0, 0, 0, .05);
  transition: border-color 160ms ease, box-shadow 160ms ease;
  -webkit-tap-highlight-color: transparent;
  @media (hover: hover) {
    &:hover {
      border-color: ${props => props.themeContext.primaryColor};
      box-shadow: 0 6px 18px rgba(0, 0, 0, .1);
    }
  }
  &:focus-visible { outline: 2px solid ${props => props.themeContext.primaryColor}; outline-offset: 3px; }
  &:active { border-color: ${props => props.themeContext.primaryColor}; }
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;

const ImageFrame = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  aspect-ratio: 1;
  background: ${props => lightTint(props.themeContext.primaryColor, .07)};
  color: ${props => readableText(lightTint(props.themeContext.primaryColor, .07), props.themeContext.primaryColor)};
`;

const CategoryImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CardFooter = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px;
  min-height: 64px;
  box-sizing: border-box;
`;

const CategoryTitle = styled.span`
  min-width: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const CategoryArrow = styled.span`
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${props => props.themeContext.primaryColor};
  color: ${props => actionTextColor(props.themeContext)};
`;
